import DOMPurify from "dompurify";
import logger from "@shared/utils/logger";

import type { NavigationGuard } from "vue-router";

const domPurifyConfig = {
  ALLOWED_TAGS: [], // Disallow all HTML tags
  ALLOWED_ATTR: [], // Disallow all attributes
  KEEP_CONTENT: false, // Remove tag content if any tag present
  RETURN_DOM: false, // Return sanitized string, not DOM nodes
};

/**
 * Sanitizes a query object by stripping potentially dangerous HTML/JS content
 * from all string and string-array values using DOMPurify.
 *
 * @param query - A raw `route.query` object from `vue-router`
 * (or any generic key-value record).
 * @returns A sanitized query object where all values are guaranteed to be
 * strings or arrays of strings with unsafe content removed.
 *
 * @example
 * ```ts
 * sanitizeQuery({ name: "<script>alert(1)</script>" }) // { name: "" }
 * ```
 */
export function sanitizeQuery(
  query: Record<string, unknown>,
): Record<string, string | string[]> {
  const sanitizedQuery: Record<string, string | string[]> = {};

  for (const key in query) {
    const value = query[key];

    if (Array.isArray(value)) {
      sanitizedQuery[key] = value.map((v) => {
        return typeof v === "string"
          ? DOMPurify.sanitize(v, domPurifyConfig)
          : "";
      });
    } else if (typeof value === "string") {
      sanitizedQuery[key] = DOMPurify.sanitize(value, domPurifyConfig);
    }
  }

  return sanitizedQuery;
}

export const queryParamSanitizationGuard: NavigationGuard = (to) => {
  const sanitizedQuery = sanitizeQuery(to.query);

  // If sanitization occurred, redirect with the sanitized query
  if (JSON.stringify(sanitizedQuery) !== JSON.stringify(to.query)) {
    logger.error(
      `Removed invalid query parameter(s) from ${JSON.stringify(to.query)}`,
    );

    return {
      ...to,
      query: sanitizedQuery,
      replace: true,
    };
  }
};
